import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthServiceInterface } from './auth.interface';
import { OAuthService, AuthConfig } from 'angular-oauth2-oidc';
import { Subject, Observable, filter } from 'rxjs';
import { environment } from '@env/environment';

// A simple interface for type-safe identity claims
export interface IdentityClaims {
  sub: string;
  name?: string;
  email?: string;
  preferred_username?: string;
  [key: string]: any; // For any other claims
}

@Injectable()
export class OIDCAuthService implements AuthServiceInterface {
  private loginSuccess = new Subject<boolean>();

  constructor(private oauthService: OAuthService, private router: Router) {}

  public async runInitialLoginSequence(): Promise<void> {
    console.debug('[Auth] issuer: ', environment.oidc?.issuer);
    const requestedScopes = environment.oidc?.scope ?? 'openid profile email';
    const requireHttps = environment.oidc?.requireHttps ?? true;
    const oidcConfig: AuthConfig = {
      clientId: environment.oidc?.clientId ?? '',
      issuer: environment.oidc?.issuer,
      redirectUri: window.location.origin,
      responseType: 'code',
      scope: requestedScopes,
      showDebugInformation: !environment.production,
      strictDiscoveryDocumentValidation: false,
      requireHttps: requireHttps
    };
    console.debug('[Auth] configure OIDC...');

    this.oauthService.configure(oidcConfig);

    this.oauthService.events
      .pipe(filter((event) => event.type === 'token_received'))
      .subscribe(() => {
        console.debug('[Auth] Token received, login successful.');
        const savedState = this.oauthService.state;
        if (savedState && savedState !== '/') {
          this.router.navigateByUrl(decodeURIComponent(savedState));
        }
        this.loginSuccess.next(true);
      });

    console.debug('[Auth] OIDC loadDiscoveryDocumentAndTryLogin...');
    try {
      await this.oauthService.loadDiscoveryDocumentAndTryLogin();
      if (this.oauthService.hasValidAccessToken()) {
        this.loginSuccess.next(true);
      } else {
        console.debug('[Auth] User is not logged in.');
      }
    } catch (error) {
      console.error('[Auth] Error during initial login sequence:', error);
    }
  }

  public login(): void {
    console.debug('[Auth] Initiating login flow.');
    const currentUrl = window.location.pathname + window.location.search;
    this.oauthService.initLoginFlow(currentUrl);
  }

  public logout(): void {
    this.oauthService.logOut();
  }

  public isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  public getUser(): IdentityClaims | null {
    return this.oauthService.getIdentityClaims() as IdentityClaims | null;
  }

  public getUserEmail(): string | undefined {
    return this.getUser()?.email;
  }

  public getUserFullName(): string | undefined {
    return this.getUser()?.name;
  }

  public getAccessToken(): string | undefined {
    return this.oauthService.getAccessToken();
  }

  public getLoginSuccessSubject(): Observable<boolean> {
    return this.loginSuccess.asObservable();
  }
}
